import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../contexts/ApiContext';
import { useAlert } from '../../contexts/AlertContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../ui/Button';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ProfileForm: React.FC = () => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [otherName, setOtherName] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
    const [mobile, setMobile] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [showGoBackButton, setShowGoBackButton] = useState(false);
    const [mobileError, setMobileError] = useState('');
    const { user } = useAuth();
    const { userProfile, loadingProfile, fetchUserProfile, updateUserProfile } = useApi();
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const location = useLocation();
    const fromTokenValidation = (location.state as { from?: string } | null)?.from === 'tokenValidation';
    const backButtonLabel = fromTokenValidation ? 'Continue →' : '← Go Back';

    const validateMobileNumber = (mobileNumber: string): boolean => {
        if (!mobileNumber) return true; // Mobile is optional, so empty is valid

        // Remove all non-digit characters for validation
        const digitsOnly = mobileNumber.replace(/\D/g, '');

        // Check if it's a valid Australian mobile number
        // Australian mobile numbers start with 04 and have 10 digits total
        // Or international format starting with +61 4 (13 digits with +)
        const australianMobileRegex = /^04\d{8}$/;
        const internationalMobileRegex = /^\+614\d{8}$/;

        // Also accept format with spaces or dashes
        const cleanedNumber = mobileNumber.replace(/[\s\-\(\)]/g, '');

        return australianMobileRegex.test(digitsOnly) ||
               internationalMobileRegex.test(cleanedNumber) ||
               (digitsOnly.length >= 10 && digitsOnly.length <= 15); // Generic international format
    };

    useEffect(() => {
        const loadProfile = async () => {
            const profile = await fetchUserProfile();

            if (profile) {
                setFirstName(profile.first_name);
                setLastName(profile.last_name);
                setOtherName(profile.other_names);
                console.log('date_of_birth:', profile.date_of_birth);
                console.log(sessionStorage.getItem('personType'));
                console.log(sessionStorage.getItem('peopleId'));

                // Convert yyyy-mm-dd string to Date object
                if (profile.date_of_birth) {
                    const [year, month, day] = profile.date_of_birth.split('-');
                    setDateOfBirth(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
                }

                setMobile(profile.mobile);
                setEmail(profile.email);

                // Show GoBack button only if firstName AND lastName are already populated
                if (profile.first_name && profile.last_name) {
                    setShowGoBackButton(true);
                }
            } else {
                // Set email from session if profile doesn't exist
                const userEmail = user?.email || sessionStorage.getItem('profileEmail') || sessionStorage.getItem('userEmail');
                if (userEmail) {
                    setEmail(userEmail);
                }
            }
        };

        loadProfile();
    }, [fetchUserProfile, user]);

    const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setMobile(value);

        // Clear error when user starts typing
        if (mobileError) {
            setMobileError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!firstName.trim() || !lastName.trim()) {
            showAlert('Please enter both first name and last name', 'error');
            return;
        }

        // Validate mobile number format if provided
        if (mobile && !validateMobileNumber(mobile)) {
            setMobileError('Please enter a valid mobile number (e.g., 0412 345 678 or +61 412 345 678)');
            return;
        }

        setLoading(true);

        // Convert Date object to yyyy-mm-dd string for API
        let dateOfBirthString = '';
        if (dateOfBirth) {
            const year = dateOfBirth.getFullYear();
            const month = String(dateOfBirth.getMonth() + 1).padStart(2, '0');
            const day = String(dateOfBirth.getDate()).padStart(2, '0');
            dateOfBirthString = `${year}-${month}-${day}`;
        }

        const profileData = {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            other_names: otherName.trim(),
            date_of_birth: dateOfBirthString,
            mobile: mobile.trim()
        };

        const success = await updateUserProfile(profileData);

        setLoading(false);

        if (success) {
            setTimeout(() => {
                navigate('/dashboard');
            }, 1500);
        }
    };

    const handleGoBack = () => {
        navigate('/dashboard');
    };

    if (loadingProfile) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                <p>Loading profile...</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} autoComplete="off" data-lpignore="true">
            <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                    type="email"
                    id="email"
                    value={email}
                    disabled
                />
            </div>

            <div className="form-group">
                <label htmlFor="firstName">
                    First Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="lastName">
                    Last Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="otherName">
                    Other Name
                </label>
                <input
                    type="text"
                    id="otherName"
                    value={otherName}
                    onChange={(e) => setOtherName(e.target.value)}
                    placeholder="Enter your other name (optional)"
                />
            </div>

            <div className="form-group" data-lpignore="true">
                <label htmlFor="field_dob_input">
                    Date of Birth
                </label>
                <div data-lpignore="true" style={{ position: 'relative' }}>
                    <DatePicker
                        id="field_dob_input"
                        selected={dateOfBirth}
                        onChange={(date: Date | null) => setDateOfBirth(date)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/yyyy"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        maxDate={new Date()}
                        yearDropdownItemNumber={100}
                        scrollableYearDropdown
                        autoComplete="off"
                        className="date-picker-input"
                        wrapperClassName="date-picker-wrapper"
                    />
                </div>
                <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                    Format: dd/mm/yyyy (e.g., 25/12/1990)
                </small>
            </div>

            <div className="form-group">
                <label htmlFor="mobile">
                    Mobile Number
                </label>
                <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={mobile}
                    onChange={handleMobileChange}
                    placeholder="e.g., 0412 345 678 or +61 412 345 678"
                    autoComplete="tel"
                    style={mobileError ? { borderColor: '#f44336' } : {}}
                />
                {mobileError && (
                    <small style={{ color: '#f44336', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                        {mobileError}
                    </small>
                )}
                {!mobileError && (
                    <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                        Format: 0412 345 678 or +61 412 345 678 (optional)
                    </small>
                )}
            </div>

            <div className="button-group">
                <Button type="submit" variant="primary" loading={loading}>
                    Save Changes
                </Button>
                {showGoBackButton && (
                    <Button type="button" variant="secondary" onClick={handleGoBack}>
                        {backButtonLabel}
                    </Button>
                )}
            </div>
        </form>
    );
};

export default ProfileForm;